import { AlertService, Button, Checkbox, Loading, MaskedTextInput, TextInput, Theme } from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';
import client from '../../apollo/client';
import AppSettings from '../../app-settings';
import routePaths from '../../route-paths';
import { InviteService, LoginService, TitleService } from '../../services';
import { Breadcrumbs } from '../../store';

const useStyles = createUseStyles({
  login__container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    maxWidth: '1170px',
    margin: '0 auto',
  },
  login__title: {
    fontSize: '1.3rem',
    marginBottom: '40px',
    textAlign: 'center',
  },
  form__row: {
    width: '360px',
    marginBottom: '15px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  form__container: {
    marginBottom: '2.8rem',
  },
  form__button: {
    width: '100%',
    textTransform: 'uppercase',
  },
  message__container: {
    width: '360px',
    borer: '1px solid #d6e9c6',
    backgroundColor: '#dff0d8',
    color: '#3c763d',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    height: '36px',
    marginBottom: '15px',
  },
  'message__container--error': {
    borer: '1px solid #ebccd1',
    backgroundColor: '#f2dede',
    color: '#a94442',
    textAlign: 'center',
  },
  formInput: {
    flexBasis: '48%',
    boxSizing: 'border-box',
    flexGrow: 1,
  },
  login__link: {
    color: '#5d9bd1',
    cursor: 'pointer',
  },
  loading__container: {
    maxHeight: '76vh',
  },
  errorMessage: {
    flexBasis: '100%',
    width: '100%',
    marginTop: -10,
    color: Theme.red,
    textAlign: 'left',
  },
});

const PASSWORD_MINIMUM_LENGTH = 8;

const RegisterInvitee = ({ match, history }) => {
  const classes = useStyles();
  const token = match.params.token;
  const [inviteDetails, setInviteDetails] = useState(null);
  const [saving, setSaving] = useState(false);

  const initializeInviteDetails = (_details) => {
    const details = {
      ..._details,
      valid: true,
      phone: '1',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
    };
    setInviteDetails(details);
  };

  const getInvite = useCallback(async (token) => {
    const res = await InviteService.getInviteConfirmation(token);
    try {
      if (!res.ok) throw new Error();
      const details = await res.json();
      initializeInviteDetails(details);
    } catch {
      const error = await res.text();
      setInviteDetails({ valid: false, error });
    }
  }, []);

  useEffect(() => {
    TitleService.setTitles('Register Invitee');
    Breadcrumbs.set([
      {
        url: `/register-invitee/${token}`,
        title: 'Register Invitee',
      },
    ]);
    getInvite(token);
  }, [token, getInvite]);

  const loading = inviteDetails === null;
  const errored = !inviteDetails?.valid;
  const expired = inviteDetails?.error === 'Invitation is expired';
  const notFound = inviteDetails?.error === 'Invitation is not found';
  const exists = inviteDetails?.error === 'User with that email address already exists';

  const updateInvite = (key, value) => {
    setInviteDetails({ ...inviteDetails, [key]: value });
  };

  const handleAgreeChecked = (update) => {
    updateInvite('agreeToTerms', update.checked);
  };

  const validations = useMemo(() => {
    if (inviteDetails === null || !inviteDetails.valid) return { valid: false };
    const { first, last, password, confirmPassword, phone } = inviteDetails;
    const _password = password.trim();
    const _confirmPassword = confirmPassword.trim();
    let firstError;
    let lastError;
    let phoneError;
    let passwordError;
    let confirmPasswordError;
    let valid = true;
    if (first === '') {
      firstError = 'Please enter your first name';
      valid = false;
    }
    if (last === '') {
      lastError = 'Please enter your last name';
      valid = false;
    }
    if (_password !== '') {
      if (_password.length < PASSWORD_MINIMUM_LENGTH) {
        passwordError = `Password must be longer than ${PASSWORD_MINIMUM_LENGTH} characters`;
        valid = false;
      }
      if (_confirmPassword !== '' && _confirmPassword !== _password) {
        confirmPasswordError = 'Passwords must match';
        valid = false;
      }
    } else valid = false;
    if (phone.length > 1 || phone === '') {
      if (phone.length < 10) {
        phoneError = 'Phone number must be at least 10 characters';
        valid = false;
      }
    }

    return {
      firstError,
      lastError,
      phoneError,
      passwordError,
      confirmPasswordError,
      valid,
    };
  }, [inviteDetails]);

  const acceptInvite = async (e) => {
    e.preventDefault();
    if (!validations.valid) return;
    if (!inviteDetails.agreeToTerms) return;
    const profile = {
      firstName: inviteDetails.first,
      lastName: inviteDetails.last,
      email: inviteDetails.email,
      title: inviteDetails.title,
      cellNumber: inviteDetails.phone,
      notifications: {
        toggle_sms: false,
        toggle_email: true,
        toggle_inapp: true,
      },
    };
    setSaving(true);
    const res = await InviteService.acceptInvite(token, inviteDetails.password.trim(), profile);
    setSaving(false);
    try {
      if (!res.ok) throw new Error();
      AlertService.alert({ type: 'success', message: 'Account Created! Now Logging In...', autoDismiss: true });
    } catch {
      const error = await res.text();
      AlertService.alert({ type: 'warning', message: error, autoDismiss: true });
      return;
    }
    try {
      await LoginService.login(inviteDetails.email, inviteDetails.password);
      await client.resetStore();
      history.push(routePaths.home);
    } catch {
      AlertService.alert({ type: 'warning', message: <span>We had a problem logging in.</span>, autoDismiss: true });
      history.push(routePaths.signIn);
    }
  };

  const renderValidation = (validationMessage) => {
    if (validationMessage) {
      return <div className={classes.errorMessage}>{validationMessage}</div>;
    }
    return null;
  };

  return (
    <div className={classes.loading__container}>
      <Loading loading={loading}>
        <div>
          {!errored && (
            <div className={classes.login__container} data-element="register-invitee">
              <h4 className={classes.login__title}>
                Welcome to Spoiler Alert! You&apos;re invited to join the
                <b> {inviteDetails.siteName}</b> team on Spoiler Alert.
                <br />
                <br />
                Fill in the details below to create your account for <b>{inviteDetails.email}</b>.
              </h4>
              <form onSubmit={acceptInvite} className={classes.form__container}>
                <div className={classes.form__row} data-element="name">
                  <TextInput
                    id="first-name"
                    className={classes.formInput}
                    onChange={updateInvite.bind(this, 'first')}
                    required
                    type="text"
                    labelText="First Name"
                    value={inviteDetails.first}
                  />
                  <TextInput
                    id="last-name"
                    className={classes.formInput}
                    onChange={updateInvite.bind(this, 'last')}
                    required
                    type="text"
                    labelText="Last Name"
                    value={inviteDetails.last}
                  />
                  {renderValidation(validations.firstError)}
                  {renderValidation(validations.lastError)}
                </div>

                <div className={classes.form__row} data-element="phone">
                  <MaskedTextInput
                    id="phone"
                    onChange={updateInvite.bind(this, 'phone')}
                    mask="+_ (___) ___-____"
                    className={classes.formInput}
                    allowedCharacters={'\\d'}
                    title="Phone number must be in format (123) 456-7890"
                    type="tel"
                    labelText="Phone"
                    value={inviteDetails.phone}
                  />
                  {renderValidation(validations.phoneError)}
                </div>

                <div className={classes.form__row} data-element="password">
                  <TextInput
                    id="password"
                    className={classes.formInput}
                    onChange={updateInvite.bind(this, 'password')}
                    required
                    type="password"
                    labelText="New Password"
                    value={inviteDetails.password}
                    pattern=".{6,}"
                    title="Your Password must be at least 6 characters"
                  />
                  {renderValidation(validations.passwordError)}
                </div>

                <div className={classes.form__row} data-element="confirm-password">
                  <TextInput
                    id="confirm-password"
                    className={classes.formInput}
                    onChange={updateInvite.bind(this, 'confirmPassword')}
                    required
                    type="password"
                    labelText="Confirm New Password"
                    value={inviteDetails.confirmPassword}
                    pattern=".{6,}"
                    title="Your Password must be at least 6 characters"
                  />
                  {renderValidation(validations.confirmPasswordError)}
                </div>

                <div className={classes.form__row} data-element="agree-to-terms">
                  <Checkbox value="agreeToTerms" id="agree-to-terms" onChecked={handleAgreeChecked} checked={inviteDetails.agreeToTerms} />
                  <span style={{ marginLeft: '10px' }}>
                    I agree to Spoiler Alert
                    <a target="_blank" rel="noopener noreferrer" className={classes.login__link} href={AppSettings.MARKETING_TERMS_URL}>
                      {' '}
                      Terms
                    </a>{' '}
                    and
                    <a target="_blank" rel="noopener noreferrer" className={classes.login__link} href={AppSettings.MARKETING_PRIVACY_URL}>
                      {' '}
                      Privacy Policy
                    </a>
                    *
                  </span>
                </div>

                <div className={classes.form__row}>
                  <Button
                    disabled={!validations.valid || !inviteDetails.agreeToTerms}
                    className={classes.form__button}
                    id="create-account"
                    type="submit"
                    loading={saving}
                    loadingText="Loading..."
                  >
                    Create my Spoiler Alert account
                  </Button>
                </div>
              </form>
            </div>
          )}
          {expired && <h3>We&apos;re sorry, but your invitation is expired.</h3>}
          {notFound && <h3>We&apos;re sorry, but the invitation was not found.</h3>}
          {exists && (
            <h3>
              The invitation was already accepted. Try <Link to={routePaths.signIn}>logging in</Link> instead.
            </h3>
          )}
        </div>
      </Loading>
    </div>
  );
};

RegisterInvitee.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object,
};

export default RegisterInvitee;
